
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface Reply {
  id: string;
  content: string;
  created_at: string;
  author: {
    id: string;
    username: string;
    avatar_url: string | null;
  };
  like_count: number;
  is_liked: boolean;
}

interface ThreadDetailProps {
  threadId: string;
  content: string;
  onClose: () => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function ThreadDetail({
  threadId,
  content,
  onClose,
}: ThreadDetailProps) {
  // Fixed generic syntax – removed HTML entities
  const [replies, setReplies] = useState<Reply[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchReplies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  const fetchReplies = async () => {
    setLoading(true);
    try {
      const { data: repliesData, error } = await supabase
        .from('forum_replies')
        .select(`
          id,
          content,
          created_at,
          author:profiles!forum_replies_author_id_fkey (
            id,
            username,
            avatar_url
          )
        `)
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (!repliesData || repliesData.length === 0) {
        setReplies([]);
        setLoading(false);
        return;
      }

      // Fetch like counts and user likes
      const replyIds = repliesData.map((r) => r.id);
      const { data: likeCounts } = await supabase
        .from('forum_reply_likes')
        .select('reply_id, user_id')
        .in('reply_id', replyIds);

      const repliesWithLikes = repliesData.map((reply) => ({
        ...reply,
        like_count:
          likeCounts?.filter((l) => l.reply_id === reply.id).length || 0,
        is_liked:
          user
            ? !!likeCounts?.some(
                (l) => l.reply_id === reply.id && l.user_id === user.id
              )
            : false,
      }));

      setReplies(repliesWithLikes);
    } catch (error) {
      console.error('Error fetching replies:', error);
      setReplies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!replyText.trim() || replyText.length > 500) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.from('forum_replies').insert({
        thread_id: threadId,
        author_id: user.id,
        content: replyText.trim(),
      });

      if (error) throw error;

      setReplyText('');
      await fetchReplies();
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditReply = async (replyId: string) => {
    if (!editText.trim() || editText.length > 500) return;

    try {
      const { error } = await supabase
        .from('forum_replies')
        .update({ content: editText.trim() })
        .eq('id', replyId);

      if (error) throw error;

      setEditingReplyId(null);
      setEditText('');
      await fetchReplies();
    } catch (error) {
      console.error('Error editing reply:', error);
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    if (!confirm('Delete this reply? This action cannot be undone.')) return;

    try {
      const { error } = await supabase
        .from('forum_replies')
        .delete()
        .eq('id', replyId);

      if (error) throw error;

      await fetchReplies();
    } catch (error) {
      console.error('Error deleting reply:', error);
    }
  };

  const handleToggleLike = async (replyId: string, isLiked: boolean) => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Optimistic UI update
    setReplies((prev) =>
      prev.map((r) =>
        r.id === replyId
          ? {
              ...r,
              is_liked: !isLiked,
              like_count: isLiked ? r.like_count - 1 : r.like_count + 1,
            }
          : r
      )
    );

    try {
      if (isLiked) {
        await supabase
          .from('forum_reply_likes')
          .delete()
          .eq('reply_id', replyId)
          .eq('user_id', user.id);
      } else {
        await supabase.from('forum_reply_likes').insert({
          reply_id: replyId,
          user_id: user.id,
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert optimistic change on failure
      setReplies((prev) =>
        prev.map((r) =>
          r.id === replyId
            ? {
                ...r,
                is_liked: isLiked,
                like_count: isLiked ? r.like_count + 1 : r.like_count - 1,
              }
            : r
        )
      );
    }
  };

  return (
    <div className="bg-zinc-900/80 border-t border-red-900/20 p-6 space-y-6">
      {/* Full Thread Content */}
      <div className="prose prose-invert max-w-none">
        <p className="text-zinc-300 font-['Crimson_Text'] leading-relaxed whitespace-pre-wrap">
          {content}
        </p>
      </div>

      {/* Replies Section */}
      <div className="border-t border-red-900/10 pt-6">
        <h4 className="text-lg font-['Cinzel'] text-zinc-100 mb-4">
          {replies.length}{' '}
          {replies.length === 1 ? 'Reply' : 'Replies'}
        </h4>

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="flex gap-3 animate-pulse">
                <div className="w-10 h-10 rounded-full bg-zinc-800"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-zinc-800 rounded w-1/4"></div>
                  <div className="h-4 bg-zinc-800 rounded w-full"></div>
                  <div className="h-4 bg-zinc-800 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : replies.length === 0 ? (
          <div className="text-center py-8">
            <i className="ri-chat-3-line text-3xl text-red-900/30 mb-2"></i>
            <p className="text-zinc-500 font-['Crimson_Text']">
              No replies yet. Be the first to share your thoughts.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {replies.map((reply) => (
              <div key={reply.id} className="flex gap-3 group">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {reply.author.avatar_url ? (
                    <img
                      src={reply.author.avatar_url}
                      alt={reply.author.username}
                      className="w-10 h-10 rounded-full object-cover border border-red-900/30"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-red-900/20 border border-red-900/30 flex items-center justify-center">
                      <i className="ri-user-line text-red-500"></i>
                    </div>
                  )}
                </div>

                {/* Reply Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-red-400 font-['Crimson_Text']">
                      {reply.author.username}
                    </span>
                    <span className="text-zinc-600 text-xs">•</span>
                    <span className="text-zinc-500 text-xs font-['Crimson_Text']">
                      {formatDate(reply.created_at)}
                    </span>

                    {/* Edit/Delete Controls */}
                    {user && user.id === reply.author.id && (
                      <div className="ml-auto flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => {
                            setEditingReplyId(reply.id);
                            setEditText(reply.content);
                          }}
                          className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteReply(reply.id)}
                          className="text-xs text-zinc-500 hover:text-red-400 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    )}
                  </div>

                  {editingReplyId === reply.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        maxLength={500}
                        className="w-full px-3 py-2 bg-zinc-900/50 border border-red-900/20 text-zinc-300 text-sm font-['Crimson_Text'] focus:outline-none focus:border-red-900/40 transition-colors resize-none"
                        rows={3}
                      />
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-zinc-500">
                          {editText.length}/500
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingReplyId(null);
                              setEditText('');
                            }}
                            className="px-3 py-1 text-xs text-zinc-400 hover:text-zinc-300 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleEditReply(reply.id)}
                            disabled={
                              !editText.trim() || editText.length > 500
                            }
                            className="px-3 py-1 bg-red-900 hover:bg-red-800 text-zinc-100 text-xs font-['Cinzel'] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-zinc-400 text-sm font-['Crimson_Text'] leading-relaxed whitespace-pre-wrap mb-2">
                        {reply.content}
                      </p>

                      {/* Like Button */}
                      <button
                        onClick={() =>
                          handleToggleLike(reply.id, reply.is_liked)
                        }
                        className={`inline-flex items-center gap-1 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                          reply.is_liked
                            ? 'text-red-400'
                            : 'text-zinc-500 hover:text-red-400'
                        }`}
                      >
                        <i
                          className={
                            reply.is_liked
                              ? 'ri-heart-fill'
                              : 'ri-heart-line'
                          }
                        ></i>
                        <span>{reply.like_count}</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reply Composer */}
      <div className="border-t border-red-900/10 pt-6">
        {user ? (
          <div className="space-y-3">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Share your thoughts from the shadows..."
              maxLength={500}
              className="w-full px-4 py-3 bg-zinc-900/50 border border-red-900/20 text-zinc-300 font-['Crimson_Text'] placeholder-zinc-600 focus:outline-none focus:border-red-900/40 transition-colors resize-none"
              rows={4}
            />
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-500">
                {replyText.length}/500
              </span>
              <button
                onClick={handleSubmitReply}
                disabled={
                  !replyText.trim() ||
                  replyText.length > 500 ||
                  submitting
                }
                className="px-6 py-2 bg-red-900 hover:bg-red-800 text-zinc-100 font-['Cinzel'] text-sm tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {submitting ? 'Posting...' : 'Post Reply'}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-6 border border-red-900/20 bg-zinc-900/30">
            <p className="text-zinc-400 font-['Crimson_Text'] mb-3">
              Sign in to join the conversation
            </p>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-6 py-2 bg-red-900 hover:bg-red-800 text-zinc-100 font-['Cinzel'] text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-login-box-line"></i>
              Sign In
            </button>
          </div>
        )}
      </div>

      {/* Close Button */}
      <div className="flex justify-center pt-4">
        <button
          onClick={onClose}
          className="inline-flex items-center gap-2 px-6 py-2 text-zinc-400 hover:text-zinc-300 font-['Cinzel'] text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-up-line"></i>
          Collapse
        </button>
      </div>
    </div>
  );
}
